import { Button, Loader, SearchField } from "@aws-amplify/ui-react";
import { API, graphqlOperation } from "aws-amplify";

import { useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import LocalizedStrings from "react-localization";
import { useParams } from "react-router-dom";
import { FiArrowLeft, FiArrowRight } from "react-icons/fi";
import moment from "moment";
import {
  CCard,
  CCardBody,
  CCollapse,
  CModal,
  CModalBody,
  CModalFooter,
  CModalHeader,
  CModalTitle,
  CTable,
} from "@coreui/react";

import { branchesByCompanyID } from "graphql/queries";
import { deleteBranch } from "graphql/mutations";
import usePermissions from "hooks/usePermission";
import { BranchCreateForm, BranchUpdateForm } from ".";

const Branches = () => {
  const [canCreate, canUpdate, canDelete] = usePermissions([
    "company-branch:create",
    "company-branch:update",
    "company-branch:delete",
  ]);
  const { companyId } = useParams();
  const [branches, setBranches] = useState([]);
  const [branchesLoading, setBranchesLoading] = useState(false);
  const [paginationTokens, setPaginationTokens] = useState({
    previous: [],
    nextToken: null,
  });
  const [detailId, setDetailId] = useState();
  const [modalDetails, setModalDetails] = useState({
    visible: false,
    branchId: null,
    branchCustomId: null,
    branchName: null,
  });
  const [modalBranch, setModalBranch] = useState({
    visible: false,
    branchId: null,
  });

  const onNext = () => {
    fetchBranches(companyId, paginationTokens.nextToken);
  };
  const onPrevious = () => {
    const previous =
      paginationTokens.previous?.length <= 1
        ? null
        : paginationTokens.previous?.slice(-2)[0];
    fetchBranches(companyId, previous, false);
  };
  const onKeywordSubmit = (keyword) => {
    const keywordSplit = keyword && keyword.split(" ");
    fetchBranches(companyId, undefined, undefined, keywordSplit);
  };
  const openModal = (action, id) =>
    setModalBranch({ visible: true, action, branchId: id });
  const onSuccessAction = () => {
    setModalBranch({ visible: false });
    fetchBranches(companyId);
  };

  async function fetchBranches(id, token, isNext = true, keywords) {
    try {
      if (!id) return;
      setBranchesLoading(true);
      const branchesData = await API.graphql(
        graphqlOperation(branchesByCompanyID, {
          companyID: id,
          limit: !keywords ? 10 : undefined,
          nextToken: token,
          filter: keywords
            ? {
                and: keywords.map((key) => ({
                  or: [
                    {
                      customId: { contains: key },
                    },
                    {
                      name: { contains: key },
                    },
                    {
                      abbreviation: { contains: key },
                    },
                    {
                      address: { contains: key },
                    },
                    {
                      phone: { contains: key },
                    },
                  ],
                })),
              }
            : {},
        })
      );
      const branchesItems = branchesData.data.branchesByCompanyID.items;
      setBranches(branchesItems);
      setPaginationTokens((prevSt) => {
        let previous = prevSt.previous ?? [];
        if (isNext) previous = token ? [...previous, token] : [...previous];
        if (!isNext && previous?.length) previous.pop();

        return {
          previous,
          nextToken: branchesData.data.branchesByCompanyID.nextToken,
        };
      });
      setDetailId(null);
      setBranchesLoading(false);
    } catch (err) {
      toast.error(strings.responses.errorFetch);
      setBranchesLoading(false);
    }
  }
  async function branchDelete(id) {
    try {
      const result = await API.graphql(
        graphqlOperation(deleteBranch, { input: { id } })
      );
      toast.success(strings.successDeleted);
      const deletedId = result.data.deleteBranch.id;
      setBranches(branches.filter((item) => item.id !== deletedId));
      setModalDetails({
        visible: false,
        branchId: null,
      });
    } catch (err) {
      toast.error(strings.responses[err]);
    }
  }

  const columnsMapped = useMemo(
    () =>
      branches.map((item) => ({
        ...item,
        id: item.customId,
        actions: (
          <>
            <Button
              className="px-1"
              children={
                detailId === item.id
                  ? strings.actions.hide
                  : strings.actions.show
              }
              variation="link"
              size="small"
              onClick={() => {
                setDetailId(detailId === item.id ? null : item.id);
              }}
            />
            {canUpdate && (
              <Button
                className="px-1"
                children={strings.actions.edit}
                variation="link"
                size="small"
                onClick={() => openModal("update", item.id)}
              />
            )}
            {canDelete && (
              <Button
                className="px-1"
                children={strings.actions.delete}
                variation="link"
                size="small"
                onClick={() => {
                  setModalDetails({
                    visible: true,
                    branchId: item.id,
                    branchCustomId: item.customId,
                    branchName: item.name,
                  });
                }}
              />
            )}
          </>
        ),
      })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [branches, detailId]
  );

  useEffect(() => {
    fetchBranches(companyId);
    setDetailId(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  const tableColumns = [
    { key: "id", label: strings.table.headers.id, _style: { width: "20%" } },
    {
      key: "name",
      label: strings.table.headers.name,
      _style: { width: "20%" },
    },
    {
      key: "abbreviation",
      label: strings.table.headers.abbreviation,
      _style: { width: "10%" },
    },
    {
      key: "address",
      label: strings.table.headers.address,
      _style: { width: "20%" },
    },
    {
      key: "phone",
      label: strings.table.headers.phone,
      _style: { width: "10%" },
    },
    {
      key: "actions",
      label: "",
      _style: { width: "20%" },
    },
  ];

  return (
    <div>
      <span className="fs-3">{strings.title}</span>
      {canCreate && (
        <Button
          style={styles.buttonAdd}
          children={strings.actions.addBranch}
          variation="primary"
          onClick={() => openModal("create")}
        />
      )}
      <div className="p-3">
        <div style={styles.searchByKeyword}>
          <SearchField
            label={strings.table.search.label}
            placeholder={strings.table.search.placeholder}
            onSubmit={onKeywordSubmit}
            onClear={onKeywordSubmit}
          />
        </div>
        {branchesLoading ? (
          <Loader />
        ) : (
          <>
            <CTable
              className="table-centered"
              items={columnsMapped}
              columns={tableColumns}
              tableHeadProps={{ color: "light" }}
              responsive
              hover
            />
            <Button
              size="small"
              disabled={paginationTokens.previous?.length <= 0}
              style={styles.buttonPrevious}
              onClick={onPrevious}
            >
              <FiArrowLeft /> {strings.table.pagination.buttonPrevious}
            </Button>
            <Button
              size="small"
              disabled={!paginationTokens.nextToken}
              style={styles.buttonNext}
              onClick={onNext}
            >
              <FiArrowRight /> {strings.table.pagination.buttonNext}
            </Button>
          </>
        )}
        <CCollapse visible={!!detailId} style={styles.collapseCard}>
          {branches
            .filter((item) => item.id === detailId)
            .map((item) => (
              <CCard key={item.id}>
                <CCardBody>
                  <h4 className="text-muted">
                    {strings.details.title} "{item.name}"
                  </h4>
                  <p className="text-muted">
                    <b>{strings.details.branchDescription}:</b>{" "}
                    {item.description}
                  </p>
                  <p className="text-muted">
                    <b>{strings.details.abbreviation}:</b> {item.abbreviation}
                  </p>
                  <p className="text-muted">
                    <b>{strings.details.address}:</b> {item.address}
                  </p>
                  <p className="text-muted">
                    <b>{strings.details.city}:</b> {item.city}
                  </p>
                  <p className="text-muted">
                    <b>{strings.details.country}:</b> {item.country}
                  </p>
                  <p className="text-muted">
                    <b>{strings.details.phone}:</b> {item.phone}
                  </p>
                  <p className="text-muted">
                    <b>{strings.details.createdAt}:</b>{" "}
                    {moment(item?.createdAt).format("YYYY-MM-DD HH:mm:ss")}
                  </p>
                </CCardBody>
              </CCard>
            ))}
        </CCollapse>
      </div>
      <CModal
        alignment="center"
        visible={modalDetails.visible}
        onClose={() => setModalDetails({ visible: false, branchId: null })}
        backdrop="static"
      >
        <CModalBody>
          <b>
            {strings.formatString(strings.modal.confirmDeleteQuestion, {
              name: modalDetails.branchName,
              id: modalDetails.branchCustomId,
            })}
          </b>
        </CModalBody>
        <CModalFooter>
          <Button
            size="small"
            onClick={() => setModalDetails({ visible: false, branchId: null })}
          >
            {strings.modal.cancelButton}
          </Button>
          <Button
            size="small"
            variation="primary"
            onClick={() => branchDelete(modalDetails.branchId)}
          >
            {strings.modal.confirmButton}
          </Button>
        </CModalFooter>
      </CModal>
      {modalBranch.action && (
        <CModal
          size="xl"
          alignment="center"
          visible={modalBranch.visible}
          onClose={() => setModalBranch({ visible: false })}
          backdrop="static"
        >
          <CModalHeader>
            <CModalTitle>
              {modalBranch.action === "create"
                ? strings.modalBranch.addTitle
                : strings.modalBranch.updateTitle}
            </CModalTitle>
          </CModalHeader>
          <CModalBody>
            {modalBranch.action === "create" ? (
              <BranchCreateForm onSuccess={onSuccessAction} />
            ) : (
              <BranchUpdateForm
                branchId={modalBranch.branchId}
                onSuccess={onSuccessAction}
              />
            )}
          </CModalBody>
        </CModal>
      )}
    </div>
  );
};

const strings = new LocalizedStrings({
  es: {
    title: "Sedes",
    successDeleted: "Sede eliminada con éxito",
    actions: {
      addBranch: "Agregar Sede",
      show: "Ver",
      hide: "Ocultar",
      edit: "Editar",
      delete: "Eliminar",
    },
    responses: {
      errorFetch: "Error al consultar sedes",
    },
    table: {
      headers: {
        id: "Identificador",
        name: "Nombre",
        abbreviation: "Abreviatura",
        address: "Dirección",
        phone: "Teléfono",
        actions: "Acciones",
      },
      pagination: {
        buttonPrevious: "Atrás",
        buttonNext: "Siguiente",
      },
      search: {
        label: "Buscar",
        placeholder: "Buscar por palabra clave...",
      },
    },
    details: {
      title: "Detalles de la sede",
      branchDescription: "Descripción",
      abbreviation: "Abreviatura",
      address: "Dirección",
      city: "Ciudad",
      country: "País",
      createdAt: "Fecha de creación",
      phone: "Teléfono",
    },
    modal: {
      confirmDeleteQuestion:
        "¿Estás seguro de eliminar la sede {name} con identificador {id}?",
      cancelButton: "Cancelar",
      confirmButton: "Confirmar",
    },
    modalBranch: {
      addTitle: "Agregar Sede",
      updateTitle: "Actualizar Sede",
    },
  },
});

const styles = {
  buttonAdd: { float: "right" },
  modal: { width: "250px" },
  collapseCard: { marginTop: "15px" },
  buttonPrevious: { marginLeft: "15px" },
  buttonNext: { float: "right", marginRight: "15px" },
  searchByKeyword: { marginBottom: "15px", width: "25%", minWidth: "300px" },
};

export default Branches;
